import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { 
  processSingleFileAsync, 
  AsyncProcessingJob 
} from '../utils/documentUpload/asyncProcessing';

interface FileProgress {
  file: File;
  status: 'pending' | 'uploading' | 'processing' | 'completed' | 'failed';
  progress: number;
  message: string;
  jobId?: string;
  job?: AsyncProcessingJob;
  error?: string;
}

const DocumentUploadPage: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [fileProgresses, setFileProgresses] = useState<FileProgress[]>([]);
  const [error, setError] = useState('');

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  if (e.target.files) {
      const files = Array.from(e.target.files);
      setSelectedFiles(files);
      setError('');
      setUploadComplete(false);
      setFileProgresses([]);
    }
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setError('Please select at least one file to upload');
      return;
    }

    setUploading(true);
    setError('');
    setUploadComplete(false);

    // Initialize progress for all files
    const initialProgresses: FileProgress[] = selectedFiles.map(file => ({
      file,
    status: 'pending',
    progress: 0,
      message: 'Waiting...'
    }));
    setFileProgresses(initialProgresses);

    try {
      // Process files one by one
    for (let i = 0; i < selectedFiles.length; i++) {
 const file = selectedFiles[i];

try {
     // Update to uploading
          setFileProgresses(prev => {
            const updated = [...prev];
        updated[i] = { ...updated[i], status: 'uploading', progress: 10, message: 'Uploading...' };
    return updated;
       });

       // Process file with async pattern
   await processSingleFileAsync(file, (progressUpdate) => {
         setFileProgresses(prev => {
    const updated = [...prev];
    updated[i] = {
      ...updated[i],
  status: progressUpdate.status as any,
        progress: progressUpdate.percent || 0,
      message: progressUpdate.message
  };
    return updated;
            });
          });

      // Mark as completed
      setFileProgresses(prev => {
  const updated = [...prev];
updated[i] = {
      ...updated[i],
              status: 'completed',
          progress: 100,
       message: 'Processing complete!'
            };
    return updated;
        });
  } catch (err: any) {
          // Mark as failed
          setFileProgresses(prev => {
            const updated = [...prev];
     updated[i] = {
  ...updated[i],
              status: 'failed',
              progress: 0,
    message: err.message || 'Processing failed',
              error: err.message
            };
            return updated;
          });
        }
      }

      setUploadComplete(true);
    } catch (err: any) {
    setError(err.message || 'An error occurred during processing');
    } finally {
setUploading(false);
    }
  };

  const handleReset = () => {
    setSelectedFiles([]);
    setFileProgresses([]);
    setUploadComplete(false);
    setError('');
  };

  const handleRetry = async (index: number) => {
    const file = selectedFiles[index];

    // Reset status
    setFileProgresses(prev => {
      const updated = [...prev];
      updated[index] = {
   ...updated[index],
        status: 'uploading',
        progress: 10,
   message: 'Retrying...',
        error: undefined
      };
      return updated;
    });

    try {
      await processSingleFileAsync(file, (progressUpdate) => {
     setFileProgresses(prev => {
          const updated = [...prev];
          updated[index] = {
            ...updated[index],
         status: progressUpdate.status as any,
    progress: progressUpdate.percent || 0,
          message: progressUpdate.message
          };
 return updated;
   });
      });

 // Mark as completed
      setFileProgresses(prev => {
        const updated = [...prev];
        updated[index] = {
 ...updated[index],
          status: 'completed',
          progress: 100,
          message: 'Processing complete!'
        };
        return updated;
 });
    } catch (err: any) {
      setFileProgresses(prev => {
        const updated = [...prev];
        updated[index] = {
      ...updated[index],
   status: 'failed',
          progress: 0,
          message: err.message || 'Processing failed',
          error: err.message
        };
        return updated;
      });
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'completed': return 'badge bg-success';
      case 'failed': return 'badge bg-danger';
      case 'processing': return 'badge bg-warning';
      case 'uploading': return 'badge bg-info';
    default: return 'badge bg-secondary';
    }
  };

  const successfulCount = fileProgresses.filter(fp => fp.status === 'completed').length;
  const failedCount = fileProgresses.filter(fp => fp.status === 'failed').length;
  const processingCount = fileProgresses.filter(fp => fp.status === 'processing' || fp.status === 'uploading').length;

  return (
    <div className="container mt-4">
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
     <h4>{t('document.upload.processing') || 'Document Upload & Processing'}</h4>
              <p className="text-muted mb-0">
    {t('upload.pdf.or.image') || 'Upload PDF or image files for automatic processing. Documents are processed asynchronously and saved as drafts.'}
        </p>
        </div>
         <div className="card-body">
   {error && (
             <div className="alert alert-danger" role="alert">
       {error}
    </div>
      )}

   {!uploadComplete && !uploading && (
             <div>
        <div className="mb-3">
       <label htmlFor="fileInput" className="form-label">
 {t('select.documents') || 'Select Documents (PDF, JPG, PNG)'}
                </label>
          <input
       type="file"
   className="form-control"
 id="fileInput"
 multiple
   accept=".pdf,.jpg,.jpeg,.png"
        onChange={handleFileChange}
          disabled={uploading}
          />
        {selectedFiles.length > 0 && (
    <div className="form-text">
  {selectedFiles.length} {t('files.selected') || 'file(s) selected'}
      </div>
           )}
        </div>

   {selectedFiles.length > 0 && (
        <div className="mb-3">
    <h6>{t('selected.files') || 'Selected Files'}:</h6>
     <ul className="list-group">
      {selectedFiles.map((file, index) => (
          <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
      {file.name}
         <span className="badge bg-secondary">{(file.size / 1024).toFixed(2)} KB</span>
 </li>
         ))}
        </ul>
               </div>
        )}

       <div className="d-flex gap-2">
         <button
   className="btn btn-primary"
          onClick={handleUpload}
          disabled={uploading || selectedFiles.length === 0}
        >
  <i className="bi bi-cloud-upload me-1"></i>
    {t('upload.and.process') || 'Upload & Process'}
     </button>
      <button
             className="btn btn-secondary"
    onClick={handleReset}
          disabled={uploading}
          >
             {t('clear') || 'Clear'}
        </button>
       </div>

           {/* Help text */}
            <div className="alert alert-info mt-3">
        <h6 className="alert-heading">
             <i className="bi bi-info-circle me-2"></i>
   {t('how.it.works') || 'How It Works'}
         </h6>
      <ul className="mb-0">
       <li>Documents are uploaded and processed in the background</li>
    <li>Each document creates a processing job that you can track</li>
          <li>Successfully processed documents are saved as draft records</li>
        <li>You can review and finalize drafts in Fuel Receipts or Shipping Records pages</li>
     <li>Check the indicator in the top-right corner to see active processing jobs</li>
  </ul>
        </div>
          </div>
       )}

       {/* Show progress while uploading */}
      {(uploading || fileProgresses.length > 0) && (
    <div>
      <div className="alert alert-info">
         <h5>
    {uploading && (
       <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
       )}
    {uploading ? (t('processing.in.progress') || 'Processing in Progress') : (t('processing.complete') || 'Processing Complete')}
        </h5>
        <p className="mb-0">
        {t('successful') || 'Successful'}: <span className="text-success fw-bold">{successfulCount}</span> | 
     {' '}{t('processing') || 'Processing'}: <span className="text-warning fw-bold">{processingCount}</span> | 
    {' '}{t('failed') || 'Failed'}: <span className="text-danger fw-bold">{failedCount}</span>
     </p>
 </div>

 {/* File progress list */}
  <div className="table-responsive">
<table className="table table-hover">
               <thead>
        <tr>
           <th style={{ width: '40%' }}>{t('file.name') || 'File Name'}</th>
               <th style={{ width: '15%' }}>{t('status') || 'Status'}</th>
     <th style={{ width: '30%' }}>{t('progress') || 'Progress'}</th>
    <th style={{ width: '15%' }}>{t('actions') || 'Actions'}</th>
           </tr>
            </thead>
             <tbody>
     {fileProgresses.map((fp, index) => (
             <tr key={index}>
    <td>
       <div>{fp.file.name}</div>
            <small className="text-muted">{(fp.file.size / 1024).toFixed(2)} KB</small>
         </td>
          <td>
         <span className={getStatusBadgeClass(fp.status)}>
       {fp.status}
        </span>
   </td>
          <td>
       <div className="progress" style={{ height: '20px' }}>
       <div
    className={`progress-bar ${fp.status === 'processing' || fp.status === 'uploading' ? 'progress-bar-striped progress-bar-animated' : ''} ${fp.status === 'failed' ? 'bg-danger' : fp.status === 'completed' ? 'bg-success' : ''}`}
    role="progressbar"
                style={{ width: `${fp.progress}%` }}
        aria-valuenow={fp.progress}
         aria-valuemin={0}
      aria-valuemax={100}
          >
    {fp.progress}%
        </div>
         </div>
     <small className="text-muted">{fp.message}</small>
 {fp.error && (
<div className="text-danger small mt-1">
            <i className="bi bi-exclamation-circle me-1"></i>
     {fp.error}
         </div>
      )}
       </td>
   <td>
       {fp.status === 'failed' && (
                <button
         className="btn btn-sm btn-warning"
              onClick={() => handleRetry(index)}
           disabled={uploading}
        >
         <i className="bi bi-arrow-clockwise me-1"></i>
       {t('retry') || 'Retry'}
       </button>
     )}
     </td>
   </tr>
           ))}
    </tbody>
           </table>
   </div>
       </div>
           )}

 {uploadComplete && (
      <div className="mt-3">
   <div className="alert alert-success">
      <i className="bi bi-check-circle me-2"></i>
        <strong>{successfulCount} document(s)</strong> processed and saved as drafts!
          <div className="mt-2">
     <i className="bi bi-info-circle me-2"></i>
      Check the <strong>indicator in the top-right corner</strong> to see your processing jobs.
    Jobs persist across page refreshes!
                    </div>
         </div>

           <div className="d-flex gap-2">
          <button
       className="btn btn-primary"
            onClick={handleReset}
               >
            {t('upload.more.documents') || 'Upload More Documents'}
  </button>
          <button
          className="btn btn-success"
onClick={() => navigate('/fuel-receipts')}
         >
           {t('view.fuel.receipts') || 'View Fuel Receipts'}
           </button>
            <button
         className="btn btn-success"
         onClick={() => navigate('/shipping')}
  >
   {t('view.shipping.records') || 'View Shipping Records'}
                    </button>
            <button
    className="btn btn-secondary"
  onClick={() => navigate('/dashboard')}
      >
 {t('back.to.dashboard') || 'Back to Dashboard'}
            </button>
       </div>
           </div>
  )}
      </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentUploadPage;
